import type { AnimationItem, RendererType } from 'lottie-web'

import { useEffect, useRef } from 'react'

interface LottieProps {
    /** Path to the animation JSON, e.g. "/lottie/Hero-Handwritten-Words-shorter.json". */
    src: string
    className?: string
    loop?: boolean
    autoplay?: boolean
    renderer?: RendererType
    speed?: number
    /** Play forward on hover, reverse on leave (decorative micro-interactions). */
    playOnHover?: boolean
    ariaLabel?: string
}

export function Lottie({
    src,
    className,
    loop = true,
    autoplay = true,
    renderer = 'svg',
    speed = 1,
    playOnHover = false,
    ariaLabel
}: LottieProps) {
    const container = useRef<HTMLDivElement>(null)
    const animRef = useRef<AnimationItem | null>(null)

    useEffect(() => {
        const el = container.current

        if (!el) return

        let anim: AnimationItem | null = null
        let cancelled = false

        // lottie-web pulls in heavy code; load it client-side only.
        import('lottie-web').then(({ default: lottie }) => {
            if (cancelled || !el) return

            anim = lottie.loadAnimation({
                container: el,
                renderer,
                loop,
                autoplay: autoplay && !playOnHover,
                path: src
            })

            anim.setSpeed(speed)
            animRef.current = anim
        })

        return () => {
            cancelled = true
            anim?.destroy()
            animRef.current = null
        }
    }, [src, loop, autoplay, renderer, speed, playOnHover])

    const hoverProps = playOnHover
        ? {
              onMouseEnter: () => {
                  animRef.current?.setDirection(1)
                  animRef.current?.play()
              },
              onMouseLeave: () => {
                  animRef.current?.setDirection(-1)
                  animRef.current?.play()
              }
          }
        : {}

    return (
        <div
            ref={container}
            className={className}
            role={ariaLabel ? 'img' : 'presentation'}
            aria-label={ariaLabel}
            aria-hidden={ariaLabel ? undefined : true}
            {...hoverProps}
        />
    )
}
